import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { getReport } from "@/lib/report-card.functions";
import { SOURCE_LABEL, type AIReportCard, type ReportPlanItem } from "@/lib/report-card";
import { Sparkles, Award, Target, Brain, ListChecks, Users, GraduationCap, Printer, CheckCircle2, AlertCircle, Clock, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/learner/reports/$reportId")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({
    meta: [
      { title: "AI Report — MAKAI" },
      { name: "description", content: "Your Makai AI learning report — strengths, areas to improve, and next best steps." },
      { property: "og:title", content: "AI Report — MAKAI" },
      { property: "og:description", content: "Personalised AI learning report with strengths, gaps, and next actions." },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ReportDetailPage,
});

function ReportDetailPage() {
  const { reportId } = Route.useParams();
  const load = useServerFn(getReport);
  const { data, isLoading, error } = useQuery({
    queryKey: ["ai-report", reportId],
    queryFn: () => load({ data: { id: reportId } }),
  });

  if (isLoading) return <AppShell role="learner" title="AI Report"><div className="p-6 text-sm text-muted-foreground">Loading…</div></AppShell>;
  if (error || !data) return <AppShell role="learner" title="AI Report"><div className="p-6 text-sm text-destructive">Report not available.</div></AppShell>;

  const r = data as AIReportCard;
  const verifyUrl = typeof window !== "undefined" ? `${window.location.origin}/learner/reports/${r.id}` : `/learner/reports/${r.id}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(verifyUrl)}`;

  return (
    <AppShell role="learner" title="AI Report">
      <div className="max-w-4xl mx-auto p-4 space-y-4 print:p-0 print:max-w-none">
        <div className="flex items-center justify-between print:hidden">
          <Link to="/learner/reports" className="text-sm text-muted-foreground hover:underline">← All reports</Link>
          <Button size="sm" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1" /> Print / Save PDF</Button>
        </div>

        {/* Branded header */}
        <div className="rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-5 print:rounded-none print:border-b print:border-t-0 print:border-x-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-primary flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> MAKAI AI Report</div>
              <h1 className="text-2xl font-semibold mt-1">{r.title}</h1>
              <div className="text-sm text-muted-foreground mt-1">
                {SOURCE_LABEL[r.source_type]}{r.subject ? ` · ${r.subject}` : ""}{r.term_label ? ` · ${r.term_label}` : ""} · {new Date(r.created_at).toLocaleDateString()}
              </div>
            </div>
            <img src={qrSrc} alt="Verification QR" className="h-20 w-20 rounded border bg-white p-1" />
          </div>
        </div>

        {/* Section 1: Learner summary */}
        <Section title="1. Learner summary" icon={Award}>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-center">
            <Stat label="Overall" value={`${r.overall_score ?? 0}/${r.total_marks ?? 0}`} />
            <Stat label="Score" value={`${r.percent ?? 0}%`} />
            <Stat label="Grade" value={r.grade ?? "—"} />
            <Stat label="Mastery" value={r.mastery_level ?? "—"} />
            <Stat label="Time" value={r.time_taken_sec ? `${Math.round(r.time_taken_sec / 60)}m` : "—"} icon={Clock} />
            <Stat label="Confidence" value={`${r.confidence ?? 0}%`} />
          </div>
          <div className="mt-3 text-xs text-muted-foreground">Completion rate: {r.completion_rate ?? 100}%</div>
        </Section>

        {/* Section 2: Strengths */}
        <Section title="2. Strengths" icon={Trophy}>
          {r.strengths.length === 0 ? (
            <p className="text-sm text-muted-foreground">Complete more work so Makai can identify your strengths.</p>
          ) : (
            <ul className="space-y-2">
              {r.strengths.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">{s.label}</div>
                    <div className="text-sm text-muted-foreground">{s.why}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Section 3: Areas to improve */}
        <Section title="3. Areas to improve" icon={Target}>
          {r.improvements.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing specific to flag this time — keep going!</p>
          ) : (
            <ul className="space-y-3">
              {r.improvements.map((imp, i) => (
                <li key={i} className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Makai recommends more practice in {imp.label}</div>
                    <div className="text-sm text-muted-foreground">{imp.why}</div>
                    <div className="text-sm mt-1">💡 {imp.suggestion}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Section 4: AI Analysis */}
        <Section title="4. Makai's analysis" icon={Brain}>
          <p className="text-sm whitespace-pre-line leading-relaxed">{r.ai_analysis}</p>
        </Section>

        {/* Section 5: Smart Working */}
        {r.smart_working && (
          <Section title="5. Smart Working analysis" icon={Sparkles}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm mb-3">
              <SmallStat label="Formula selection" value={r.smart_working.correct_formula_selection ? "Good" : "Needs work"} />
              <SmallStat label="Reasoning" value={r.smart_working.good_reasoning ? "Good" : "Needs work"} />
              <SmallStat label="Calculation slips" value={String(r.smart_working.calculation_mistakes)} />
              <SmallStat label="Unit errors" value={String(r.smart_working.unit_errors)} />
              <SmallStat label="Skipped steps" value={String(r.smart_working.skipped_steps)} />
            </div>
            <ul className="text-sm space-y-1 list-disc pl-5">
              {r.smart_working.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </Section>
        )}

        {/* Section 6: Next learning plan */}
        <Section title="6. Your next learning plan" icon={ListChecks}>
          <ul className="grid gap-2">
            {r.next_plan.map((p: ReportPlanItem, i: number) => (
              <li key={i} className="rounded-lg border p-3 flex items-start gap-3">
                <span className="rounded bg-primary/10 text-primary text-xs px-2 py-0.5 uppercase tracking-wide">{p.kind}</span>
                <div className="flex-1">
                  <div className="font-medium">{p.label}</div>
                  <div className="text-sm text-muted-foreground">{p.reason}</div>
                </div>
                {p.href && (
                  <Link to={p.href} className="text-sm text-primary hover:underline shrink-0 print:hidden">Open →</Link>
                )}
              </li>
            ))}
          </ul>
        </Section>

        {/* Section 7: Parent version */}
        {r.parent_version && (
          <Section title="7. Note for parents" icon={Users}>
            <p className="text-sm whitespace-pre-line">{r.parent_version}</p>
          </Section>
        )}

        {/* Section 8: Teacher version */}
        {r.teacher_version && (
          <Section title="8. Note for teachers" icon={GraduationCap}>
            <p className="text-sm whitespace-pre-line">{r.teacher_version}</p>
          </Section>
        )}

        {/* Section 9: PDF signature block */}
        <Section title="9. Comments & verification" icon={Award}>
          <div className="grid gap-4 md:grid-cols-2 text-sm">
            <div>
              <div className="font-medium mb-1">Teacher comments</div>
              <div className="border-b border-dashed h-6" />
              <div className="border-b border-dashed h-6 mt-2" />
              <div className="border-b border-dashed h-6 mt-2" />
            </div>
            <div>
              <div className="font-medium mb-1">Parent signature</div>
              <div className="border-b border-dashed h-6" />
              <div className="mt-3 text-xs text-muted-foreground">Verify this report at:<br /><span className="break-all">{verifyUrl}</span></div>
            </div>
          </div>
        </Section>

        <div className="text-center text-xs text-muted-foreground py-4 print:pt-6">Generated by MAKAI Academy · makaiacademy.lovable.app</div>
      </div>
    </AppShell>
  );
}

function Section({ title, icon: Icon, children }: { title: string; icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border bg-card p-5 print:break-inside-avoid print:rounded-none">
      <h2 className="flex items-center gap-2 text-base font-semibold mb-3"><Icon className="h-4 w-4 text-primary" /> {title}</h2>
      {children}
    </section>
  );
}

function Stat({ label, value, icon: Icon }: { label: string; value: string; icon?: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="rounded-lg bg-muted/40 p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function SmallStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border p-2">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
